import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Mic, Pause, Play, RotateCcw, Square } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { Modal } from "../../../../shared/ui/modal";
import { useAudioPeaks } from "../../application/useAudioPeaks";
import { useMeditationAudioUpload } from "../../application/useMeditationAudio";
import { useMeditationRecorder } from "../../application/useMeditationRecorder";
import { useStudioPreview } from "../../application/useStudioPreview";
import { BACKGROUND_TRACK_GAIN } from "../../domain/backgroundTracks";
import { BackgroundTrackId, formatDuration } from "../../domain/types";
import BackgroundTrackPicker from "./BackgroundTrackPicker";
import Waveform from "./Waveform";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    /** The bed the meditation carries today, so reopening starts where it was. */
    backgroundTrack: BackgroundTrackId;
    /** …and which of the therapist's own tracks it is, when it is one. */
    backgroundTrackId?: string | null;
    /** The mix the meditation carries today, so reopening starts where it was. */
    voiceVolume?: number;
    backgroundVolume?: number;
    /** Handed the stored recording, the bed, and how loud each one sits. */
    onDone: (result: {
        audio: { path: string; durationSeconds: number };
        backgroundTrack: BackgroundTrackId;
        backgroundTrackId: string | null;
        voiceVolume: number;
        backgroundVolume: number;
    }) => void;
    /** Present once the meditation is saved, so the upload is attributed. */
    meditationId?: string;
    /** A centre meditation: music uploaded while recording goes to the centre. */
    forCenter?: boolean;
};

/**
 * Recording a meditation, as two lanes rather than a button.
 *
 * A therapist who has just recorded four minutes of their own voice has one
 * question — did that come out right — and a bare "grabar / usar esta toma"
 * pair answers none of it. So the take is drawn: the voice as a waveform they
 * can scrub, the bed as a choice made *after* hearing what it sits under, and
 * a play button that sounds the two together exactly as the session will.
 *
 * The order matters. The bed used to be picked before the first word was
 * spoken and mixed into the recording as it was captured, which meant
 * re-recording to change your mind. Here the recording is only the voice, and
 * the bed travels beside it as a choice that can be heard, changed and heard
 * again.
 */
function Fader({
    label,
    value,
    onChange,
}: {
    label: string;
    value: number;
    onChange: (next: number) => void;
}) {
    return (
        <label className="flex min-w-0 flex-col gap-1 text-xs text-content-secondary">
            <span className="flex items-center justify-between gap-2">
                {label}
                <span className="tabular-nums text-content-tertiary">
                    {Math.round(value * 100)}%
                </span>
            </span>
            <input
                type="range"
                min={0}
                max={100}
                value={Math.round(value * 100)}
                onChange={(e) => onChange(Number(e.target.value) / 100)}
                className="h-11 w-full accent-[var(--color-accent)]"
            />
        </label>
    );
}

export default function MeditationStudio({
    isOpen,
    onClose,
    backgroundTrack,
    backgroundTrackId = null,
    voiceVolume = 1,
    backgroundVolume = BACKGROUND_TRACK_GAIN,
    onDone,
    meditationId,
    forCenter = false,
}: Props) {
    const { t } = useTranslation();
    const recorder = useMeditationRecorder();
    const { uploading, error: uploadError, upload } = useMeditationAudioUpload();
    const [bed, setBed] = useState<BackgroundTrackId>(backgroundTrack);
    const [bedTrackId, setBedTrackId] = useState<string | null>(backgroundTrackId);
    /*
     * The two faders. A quarter under a full voice is a decent starting point
     * and a bad rule: a soft voice wants the bed lower, and slow piano carries
     * more than an ocean does.
     */
    const [voiceLevel, setVoiceLevel] = useState(voiceVolume);
    const [bedLevel, setBedLevel] = useState(backgroundVolume);

    const take = recorder.result;
    const peaks = useAudioPeaks(take?.blob ?? null);
    const preview = useStudioPreview(
        take?.url ?? null,
        bed,
        take?.durationSeconds ?? 0,
        bedTrackId,
        voiceLevel,
        bedLevel
    );

    // Reopening starts from the meditation's own bed rather than from whatever
    // was tried last time and abandoned.
    useEffect(() => {
        if (!isOpen) return;
        setBed(backgroundTrack);
        setBedTrackId(backgroundTrackId);
        setVoiceLevel(voiceVolume);
        setBedLevel(backgroundVolume);
    }, [isOpen, backgroundTrack, backgroundTrackId, voiceVolume, backgroundVolume]);

    const keep = async () => {
        if (!take) return;
        preview.stop();
        const path = await upload(take.blob, meditationId);
        onDone({
            audio: { path, durationSeconds: take.durationSeconds },
            backgroundTrack: bed,
            backgroundTrackId: bedTrackId,
            voiceVolume: voiceLevel,
            backgroundVolume: bedLevel,
        });
        recorder.discard();
        onClose();
    };

    const leave = () => {
        preview.stop();
        onClose();
    };

    const progress =
        take && take.durationSeconds > 0
            ? preview.position / take.durationSeconds
            : 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={leave}
            showCloseButton={false}
            className="mx-4 max-w-2xl rounded-3xl"
        >
            <div className="flex max-h-[85vh] min-w-0 flex-col gap-5 overflow-y-auto overflow-x-hidden p-5">
                <div>
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                        {t("meditation.studio.title")}
                    </h2>
                    <p className="text-sm text-content-tertiary">
                        {t("meditation.studio.subtitle")}
                    </p>
                </div>

                <section
                    aria-label={t("meditation.studio.voiceLane")}
                    className="min-w-0 rounded-2xl border border-border-subtle p-4"
                >
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-content-primary">
                            {t("meditation.studio.voiceLane")}
                        </h3>
                        <span
                            role="status"
                            className="text-sm tabular-nums text-content-secondary"
                        >
                            {formatDuration(
                                recorder.recording
                                    ? recorder.elapsedSeconds
                                    : (take?.durationSeconds ?? 0)
                            )}
                        </span>
                    </div>

                    {/*
                      * While the microphone is open the lane draws what it is
                      * hearing; afterwards it draws the take. A recorder that
                      * shows nothing until you stop is one you cannot tell is
                      * working until four minutes are gone.
                      */}
                    <Waveform
                        peaks={recorder.recording ? recorder.levels : peaks}
                        progress={recorder.recording ? 1 : progress}
                        onSeek={take && !recorder.recording ? preview.seekTo : undefined}
                    />

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {recorder.recording ? (
                            <>
                                <Button
                                    variant="destructive"
                                    startIcon={<Square size={16} />}
                                    onClick={recorder.stop}
                                >
                                    {t("meditation.audio.stop")}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={
                                        recorder.paused
                                            ? recorder.resume
                                            : recorder.pause
                                    }
                                >
                                    {t(
                                        recorder.paused
                                            ? "meditation.audio.resume"
                                            : "meditation.audio.pause"
                                    )}
                                </Button>
                                <span
                                    aria-hidden="true"
                                    className="size-2.5 animate-pulse rounded-full bg-status-critical-fill"
                                />
                            </>
                        ) : take ? (
                            <>
                                <Button
                                    variant="outline"
                                    startIcon={
                                        preview.playing ? (
                                            <Pause size={16} />
                                        ) : (
                                            <Play size={16} />
                                        )
                                    }
                                    onClick={
                                        preview.playing ? preview.pause : preview.play
                                    }
                                >
                                    {t(
                                        preview.playing
                                            ? "meditation.studio.pause"
                                            : "meditation.studio.play"
                                    )}
                                </Button>
                                <Button
                                    variant="ghost"
                                    startIcon={<RotateCcw size={16} />}
                                    onClick={() => {
                                        preview.stop();
                                        recorder.discard();
                                    }}
                                >
                                    {t("meditation.studio.again")}
                                </Button>
                            </>
                        ) : (
                            <Button
                                variant="outline"
                                startIcon={<Mic size={16} />}
                                onClick={() => void recorder.start()}
                            >
                                {t("meditation.audio.record")}
                            </Button>
                        )}
                    </div>

                    {recorder.error && (
                        <p role="alert" className="mt-3 text-sm text-status-critical">
                            {t(`meditation.audio.errors.${recorder.error}`)}
                        </p>
                    )}
                </section>

                {/*
                  * Second, and deliberately below the take: the bed is heard
                  * against a voice that already exists, which is the only way
                  * to tell whether it is the right one.
                  */}
                <section
                    aria-label={t("meditation.studio.bedLane")}
                    className="min-w-0 rounded-2xl border border-border-subtle p-4"
                >
                    <BackgroundTrackPicker
                        forCenter={forCenter}
                        value={bed}
                        trackId={bedTrackId}
                        onChange={({ track, trackId }) => {
                            setBed(track);
                            setBedTrackId(trackId);
                        }}
                    />

                    {/*
                      * Two faders rather than one balance control: a therapist
                      * says "my voice is quiet" or "this music is loud", not
                      * "the ratio is wrong". Both move what is already
                      * playing, so the mix is heard while it is set.
                      */}
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        <Fader
                            label={t("meditation.studio.voiceVolume")}
                            value={voiceLevel}
                            onChange={setVoiceLevel}
                        />
                        <Fader
                            label={t("meditation.studio.bedVolume")}
                            value={bedLevel}
                            onChange={setBedLevel}
                        />
                    </div>
                </section>

                {uploadError && (
                    <p role="alert" className="text-sm text-status-critical">
                        {t("meditation.audio.uploadFailed")}
                    </p>
                )}

                <div className="flex flex-wrap items-center justify-end gap-2">
                    {uploading && (
                        <span className="flex items-center gap-2 text-sm text-content-secondary">
                            <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                            {t("meditation.audio.uploading")}
                        </span>
                    )}
                    <Button variant="ghost" onClick={leave}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        onClick={() => void keep()}
                        disabled={!take || recorder.recording}
                        loading={uploading}
                    >
                        {t("meditation.studio.keep")}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
